import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type { AdminUser, LoginCredentials, AuthUser } from '@/types'
import { apiClient } from '@/utils/api'

export const useAuthStore = defineStore('auth', () => {
  // State
  const user = ref<AdminUser | null>(null)
  const token = ref<string | null>(null)
  const isLoading = ref(false)
  const error = ref<string | null>(null)
  const totpRequired = ref(false)
  const pendingTotpToken = ref<string | null>(null)
  const pendingLoginCredentials = ref<LoginCredentials | null>(null)

  // Getters
  const isAuthenticated = computed(() => !!user.value && !!token.value)
  const userRole = computed(() => user.value?.role)
  const userPermissions = computed(() => user.value?.permissions || [])

  // Actions
  async function login(credentials: LoginCredentials, rememberMe: boolean = false): Promise<void> {
    isLoading.value = true
    error.value = null

    try {
      const response = await apiClient.post<AuthUser>('/auth/admin/login', {
        ...credentials,
        rememberMe
      })
      
      const data = response.data

      if (data?.requiresTOTP && data?.pendingToken) {
        totpRequired.value = true
        pendingTotpToken.value = data.pendingToken
        pendingLoginCredentials.value = credentials
        return
      }

      const userData = data?.user
      const isValidUser = userData && typeof userData === 'object' && !Array.isArray(userData) &&
        'email' in userData && 'role' in userData
      if (isValidUser) {
        user.value = userData as AdminUser
        token.value = 'cookie-stored'
        totpRequired.value = false
        pendingLoginCredentials.value = null
        
        localStorage.removeItem('akuo_admin_token')
        localStorage.removeItem('akuo_remember_me')
        sessionStorage.removeItem('akuo_admin_token')
        
        user.value.lastLoginAt = new Date()
      }
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Login failed. Please try again.'
      throw err
    } finally {
      isLoading.value = false
    }
  }

  async function logout(): Promise<void> {
    try {
      if (token.value) {
        await apiClient.post('/auth/admin/logout')
      }
    } catch (err) {
      console.error('Logout error:', err)
    } finally {
      // Clear state regardless of API call success
      user.value = null
      token.value = null
      error.value = null
      
      // Clear stored auth from all storages (cleanup)
      localStorage.removeItem('akuo_admin_token')
      localStorage.removeItem('akuo_remember_me')
      sessionStorage.removeItem('akuo_admin_token')
      await clearAuth()
    }
  }

  async function initializeAuth(): Promise<void> {
    // Prevent multiple simultaneous initialization attempts
    if (isLoading.value) {
      return
    }
    
    isLoading.value = true
    
    try {
      // Try to get user data using cookie-based authentication
      // The cookie is automatically sent by the browser
      const response = await apiClient.get<AdminUser>('/auth/admin/me')
      
      // Validate response is a real AdminUser object (reject HTML/index.html from misconfigured proxy)
      const data = response.data
      const isValidUser = data && typeof data === 'object' && !Array.isArray(data) &&
        'email' in data && 'role' in data
      
      if (isValidUser) {
        token.value = 'cookie-stored' // Placeholder to indicate authentication
        user.value = data as AdminUser
        error.value = null
        
        // Clear any old localStorage tokens (cleanup)
        localStorage.removeItem('akuo_admin_token')
        localStorage.removeItem('akuo_remember_me')
        sessionStorage.removeItem('akuo_admin_token')
        return
      }
      
    } catch (err) {
      console.log('No valid session found, user needs to login')
      // Clear state
      user.value = null
      token.value = null
      error.value = null
      
      // Clear any old tokens (cleanup)
      localStorage.removeItem('akuo_admin_token')
      localStorage.removeItem('akuo_remember_me')
      sessionStorage.removeItem('akuo_admin_token')
    } finally {
      isLoading.value = false
    }
  }

  async function refreshToken(): Promise<void> {
    try {
      const response = await apiClient.post<AuthUser>('/auth/admin/refresh')
      
      if (response.data) {
        // With cookie-based auth, the new token is set in the cookie by the server
        token.value = 'cookie-stored'
        user.value = response.data.user
      }
    } catch (err) {
      console.error('Token refresh error:', err)
      await logout()
      throw err
    }
  }

  function hasPermission(resource: string, action: string): boolean {
    if (!user.value) return false
    
    return userPermissions.value.some(permission => 
      permission.resource === resource && permission.actions.includes(action)
    )
  }

  function hasRole(role: string): boolean {
    return user.value?.role === role
  }

  // Helper functions
  async function clearAuth(): Promise<void> {
    // Clear any remaining localStorage tokens
    localStorage.removeItem('akuo_admin_token')
    localStorage.removeItem('akuo_remember_me')
    sessionStorage.removeItem('akuo_admin_token')
  }

  async function submitOtp(otp: string): Promise<void> {
    if (!pendingTotpToken.value) {
      throw new Error('No pending 2FA session.')
    }
    isLoading.value = true
    error.value = null
    try {
      const response = await apiClient.post<AuthUser>('/auth/admin/login/totp', {
        pendingToken: pendingTotpToken.value,
        code: otp,
      })
      const data = response.data
      const userData = data?.user
      const isValidUser = userData && typeof userData === 'object' && !Array.isArray(userData) &&
        'email' in userData && 'role' in userData
      if (isValidUser) {
        user.value = userData as AdminUser
        token.value = 'cookie-stored'
        totpRequired.value = false
        pendingTotpToken.value = null
        pendingLoginCredentials.value = null
        user.value.lastLoginAt = new Date()
      }
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Invalid code.'
      throw err
    } finally {
      isLoading.value = false
    }
  }

  // Clear error when user starts typing
  function clearError(): void {
    error.value = null
  }

  return {
    // State
    user,
    token,
    isLoading,
    error,
    
    // Getters
    isAuthenticated,
    userRole,
    userPermissions,
    
    // Actions
    login,
    logout,
    initializeAuth,
    refreshToken,
    hasPermission,
    hasRole,
    clearError,
    submitOtp,
    totpRequired,
    pendingLoginCredentials
  }
})
